import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import routesfe from '../Config/RoutesFE';
import configDetails from '../Config/Config';
import Toast from '../components copy/Toast';
import logger from '../utils/Logger';
import SEO from '../components copy/SEO';

const COMPONENT_NAME = 'LoginPage';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Extract redirect path and plan data from location state
  const from = location.state?.from?.pathname || new URLSearchParams(location.search).get('redirect') || routesfe.HOME;
  const redirectPath = location.state?.redirect;
  const selectedPlan = location.state?.selectedPlan;
  const verificationSuccess = location.state?.verificationSuccess;
  const verifiedEmail = location.state?.verifiedEmail;

  // Auto-fill email and show toast if verification was successful
  React.useEffect(() => {
    if (verificationSuccess && verifiedEmail) {
      setEmail(verifiedEmail);
      setToast({ message: 'Email verified successfully! Please log in now. 🎉', type: 'success' });
      
      // Clear the verification state from location to prevent toast from showing again on refresh
      navigate(location.pathname, { 
        replace: true, 
        state: { 
          ...location.state, 
          verificationSuccess: undefined, 
          verifiedEmail: undefined 
        } 
      });
    }
  }, [verificationSuccess, verifiedEmail, navigate, location.pathname, location.state]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    logger.info('User login attempt started', {
      component: COMPONENT_NAME,
      action: 'LOGIN_ATTEMPT',
      context: {
        email,
        timestamp: Date.now()
      }
    });

    try {
      // Clear localStorage before login, preserving calendar tokens
      const calendarKeys = ['google_access_token', 'google_refresh_token'];
      const savedTokens = {};
      
      // Save calendar tokens before clearing
      calendarKeys.forEach(key => {
        const value = localStorage.getItem(key);
        if (value) {
          savedTokens[key] = value;
        }
      });
      
      // Clear all localStorage
      localStorage.clear();
      sessionStorage.removeItem('noteforge_trial_activated');
      
      // Restore calendar tokens
      Object.entries(savedTokens).forEach(([key, value]) => {
        localStorage.setItem(key, value);
      });
      
      // Clear any existing user data from localStorage
      localStorage.removeItem('user');
      const user = await Auth.signIn(email, password);
      // Force refresh authentication state to avoid cached data
      await Auth.currentSession();
      const updatedUser = await Auth.currentAuthenticatedUser({ bypassCache: true });
      
      if (updatedUser.attributes?.email_verified) {
        const userInfo = {
          name: updatedUser.attributes?.name || 'User',
          email: updatedUser.attributes?.email || email,
          userSub: updatedUser.attributes?.sub,
        };
        localStorage.setItem('user', JSON.stringify(userInfo));

        // Check Cognito custom attributes for subscription and trial status
        const isSubscribed = updatedUser.attributes['custom:is_subscribed'] === "1";
        const isTrialUser = updatedUser.attributes['custom:is_trial_user'] === "1";

        logger.info('User login successful', {
          component: COMPONENT_NAME,
          action: 'LOGIN_SUCCESS',
          userId: userInfo.userSub,
          context: {
            email: userInfo.email,
            emailVerified: true,
            isSubscribed,
            isTrialUser,
            timestamp: Date.now()
          }
        });

        // Use setTimeout to ensure authentication state is fully set before navigation
        setTimeout(() => {
          // Priority 1: Check if there's a stored redirect path after login (for record page) - FIRST PRIORITY
          const storedRedirectPath = localStorage.getItem('redirect_after_login');
                    
          if (storedRedirectPath) {
            localStorage.removeItem('redirect_after_login');
            navigate(storedRedirectPath, { replace: true });
            return; // Exit early to prevent other redirects
          }
          
          // Priority 2: If user is a trial user (is_trial_user = 1) and not subscribed, redirect to home
          if (isTrialUser && !isSubscribed) {
            navigate(routesfe.HOME, { replace: true });
          }
          // Priority 3: If user is not subscribed and not on trial, redirect to plans page
          else if (!isSubscribed) {
            navigate(routesfe.PLANS, { replace: true });
          } 
          // Priority 4: If user is subscribed but has a payment redirect with plan (upgrade/downgrade scenario)
          else if (isSubscribed && redirectPath === routesfe.PAYMENT && selectedPlan) {
            navigate(routesfe.PAYMENT, { 
              state: { selectedPlan: selectedPlan }, 
              replace: true 
            });
          }
          // Priority 5: Default to home page for subscribed users
          else if (isSubscribed) {
            navigate(routesfe.HOME, { replace: true });
          }
        }, 100); // Small delay to ensure state is set
      } else {
        // User needs email verification
        logger.warn('Email not verified - redirecting to verification', {
          component: COMPONENT_NAME,
          action: 'EMAIL_NOT_VERIFIED',
          context: {
            email,
            timestamp: Date.now()
          }
        });
        navigate(routesfe.USER_VERIFICATION, { 
          state: { email },
          replace: true 
        });
      }
    } catch (err) {
      // Check if user is not confirmed but auto-verification is enabled
      if (err.code === 'UserNotConfirmedException' && configDetails.cognitoUserAutoVerification === 'true') {
        logger.warn('User not confirmed - redirecting to OTP verification', {
          component: COMPONENT_NAME,
          action: 'USER_NOT_CONFIRMED',
          context: {
            email,
            errorCode: err.code,
            errorName: err.name,
            errorMessage: err.message,
            autoVerificationEnabled: true,
            timestamp: Date.now()
          }
        });
        // Redirect to post-login verification page
        navigate(routesfe.USER_VERIFICATION, {
          state: { 
            email,
            isPostLoginVerification: true,
            fromLogin: true
          },
          replace: true
        });
        setLoading(false);
        return;
      }
      
      logger.error('User login failed', {
        component: COMPONENT_NAME,
        action: 'LOGIN_FAILED',
        error: err,
        context: {
          email,
          errorCode: err.code,
          errorName: err.name,
          errorMessage: err.message,
          errorStack: err.stack,
          timestamp: Date.now()
        }
      });
      
      setError(err.message || 'Login failed. Please try again.');
      // Clear any partial data on error
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Login to NoteForge"
        description="Sign in to your NoteForge account to access your AI-powered notes, transcriptions, and summaries."
        noindex={true}
      />
      <Toast toast={toast} onClose={() => setToast(null)} />
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-200 via-indigo-200 to-teal-100 animate-gradient-x relative px-2">
      {/* Animated Gradient Glow */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute -top-36 -left-36 w-96 h-96 bg-indigo-300 rounded-full opacity-30 blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-24 right-0 w-80 h-80 bg-teal-100 rounded-full opacity-30 blur-2xl"></div>
      </div>
      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white/90 backdrop-blur border border-gray-200 shadow-2xl rounded-2xl px-7 py-10 sm:px-10 flex flex-col items-center transition-all duration-300">
          {/* Logo Section */}
          <div className="w-16 h-16 mb-2 flex items-center justify-center rounded-full bg-gradient-to-br from-indigo-400 via-blue-400 to-teal-300 shadow-lg">
            {/* Replace this svg with your logo if you want */}
            <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 2a6 6 0 016 6v2a6 6 0 11-12 0V8a6 6 0 016-6zm0 14a4 4 0 004-4v-2H6v2a4 4 0 004 4z" />
            </svg>
          </div>
          <h2 className="text-3xl font-extrabold text-center text-indigo-700 drop-shadow mb-4">Welcome Back</h2>
          <p className="mb-6 text-gray-500 text-sm text-center">Sign in to your account</p>

          {/* Error message */}
          {error && (
            <div className="flex items-center justify-center gap-2 mb-6 px-3 py-2 bg-red-50 border border-red-300 text-red-600 rounded shadow-sm text-sm animate-shake">
              <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-.89-8.88v2a1 1 0 102 0v-2a1 1 0 00-2 0zm0-3a1 1 0 112 0 1 1 0 01-2 0z" clipRule="evenodd" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleLogin} className="w-full space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                className="block w-full rounded-lg border bg-white border-gray-300 px-4 py-2 shadow-inner placeholder-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-300 outline-none transition duration-200 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
               <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="block w-full rounded-lg border bg-white border-gray-300 px-4 py-2 pr-12 shadow-inner placeholder-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-300 outline-none transition duration-200 sm:text-sm"
                  style={{
                    WebkitTextSecurity: showPassword ? 'none' : undefined,
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-indigo-600 transition-colors duration-200 focus:outline-none"
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full flex justify-center items-center rounded-lg py-2 font-semibold text-white transition relative shadow-md overflow-hidden group
                ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-indigo-600 via-blue-500 to-teal-400 hover:from-indigo-700 hover:via-blue-600 hover:to-teal-500'}
              `}
            >
              <span className="relative z-10">
                {loading ? 'Logging in...' : (selectedPlan ? `Login & Subscribe to ${selectedPlan.name}${selectedPlan.billingCycle === 'YEARLY' ? ' (Annual)' : selectedPlan.billingCycle === 'MONTHLY' ? ' (Monthly)' : ''}` : 'Login')}
              </span>
              {/* Ripple animation effect */}
              <span className="absolute left-1/2 top-1/2 w-0 h-0 group-active:w-56 group-active:h-56 bg-white opacity-10 rounded-full transform -translate-x-1/2 -translate-y-1/2 transition-all duration-300"></span>
            </button>
          </form>

          {/* Navigation links */}
          <div className="flex flex-col xs:flex-row justify-between items-center gap-2 mt-6 w-full text-sm">
            <button
              type="button"
              onClick={() => {
                // Preserve plan data when navigating to signup
                if (redirectPath === routesfe.PAYMENT && selectedPlan) {
                  navigate(routesfe.SIGNUP, { 
                    state: { 
                      redirect: redirectPath, 
                      selectedPlan: selectedPlan 
                    } 
                  });
                } else {
                  navigate(routesfe.SIGNUP);
                }
              }}
              className="text-indigo-600 hover:underline hover:text-indigo-800 font-medium transition"
            >
              Create an Account
            </button>
            <button
              type="button"
              onClick={() => navigate(routesfe.FORGOT_PASSWORD)}
              className="text-indigo-500 hover:underline hover:text-indigo-700 transition"
            >
              Forgot Password?
            </button>
          </div>

          {/* Terms and Privacy Policy Agreement */}
          <div className="mt-4 text-center text-xs text-gray-500">
            By using Noteforge you agree to the{' '}
            <button
              type="button"
              onClick={() => navigate(routesfe.TERMS_OF_SERVICE)}
              className="text-indigo-500 hover:text-indigo-700 hover:underline focus:outline-none"
            >
              Terms of Service
            </button>
            {' '}and{' '}
            <button
              type="button"
              onClick={() => navigate(routesfe.PRIVACY_POLICY)}
              className="text-indigo-500 hover:text-indigo-700 hover:underline focus:outline-none"
            >
              Privacy Policy
            </button>
          </div>

          {/* Back to Home */}
          <button
            type="button"
            onClick={() => navigate(routesfe.LANDING)}
            className="mt-4 text-gray-500 hover:text-indigo-600 hover:underline transition font-medium text-sm tracking-wide"
          >
            ← Back to Home
          </button>
        </div>
      </div>
      <style>
        {`
        @keyframes gradient-x {
          0%, 100% {background-position: 0% 50%;}
          50% {background-position: 100% 50%;}
        }
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 9s ease-in-out infinite;
        }
        @keyframes shake {
          0% { transform: translateX(0); }
          12% { transform: translateX(-3px); }
          24% { transform: translateX(4px); }
          36% { transform: translateX(-4px); }
          48% { transform: translateX(4px); }
          60% { transform: translateX(-3px); }
          72%, 100% { transform: translateX(0); }
        }
        .animate-shake {
          animation: shake 0.55s cubic-bezier(.36,.07,.19,.97) both;
          }
           input[type="password"]::-ms-reveal,
        input[type="password"]::-ms-clear {
          display: none;
        }
        `}
      </style>
      </div>
    </>
  );
};

export default LoginPage;
